import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const ease = [0.22, 1, 0.36, 1];

// macOS window chrome
function WindowChrome({ title, color = 'purple' }) {
  const COLORS = {
    purple: { dot: '#635BFF', bg: 'rgba(99,91,255,0.06)', border: 'rgba(99,91,255,0.15)' },
    blue:   { dot: '#0073E6', bg: 'rgba(0,115,230,0.06)', border: 'rgba(0,115,230,0.15)' },
    cyan:   { dot: '#00C7FF', bg: 'rgba(0,199,255,0.06)', border: 'rgba(0,199,255,0.15)' },
    pink:   { dot: '#FC47BD', bg: 'rgba(252,71,189,0.06)', border: 'rgba(252,71,189,0.15)' },
    teal:   { dot: '#00D4AA', bg: 'rgba(0,212,170,0.06)', border: 'rgba(0,212,170,0.15)' },
  };
  const c = COLORS[color] || COLORS.purple;
  return (
    <div className="flex items-center justify-between px-3.5 py-2.5 border-b" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
        <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
        <div className="w-3 h-3 rounded-full bg-[#28C840]" />
      </div>
      <span className="text-[11px] font-medium" style={{ color: '#9CA3AF' }}>{title}</span>
      <div className="w-12" />
    </div>
  );
}

// Code line component
function CodeLine({ indent = 0, keyword, value, comment, dim }) {
  return (
    <div className="flex" style={{ paddingLeft: indent * 16, opacity: dim ? 0.4 : 1 }}>
      <span style={{ color: '#9CA3AF', fontSize: 11, minWidth: 24, userSelect: 'none' }}></span>
      <span style={{ fontSize: 12, fontFamily: "'JetBrains Mono', 'Fira Code', monospace", whiteSpace: 'pre' }}>
        {keyword && <span style={{ color: '#635BFF' }}>{keyword} </span>}
        {value && <span style={{ color: '#0A0527' }}>{value}</span>}
        {comment && <span style={{ color: '#9CA3AF' }}>{comment}</span>}
      </span>
    </div>
  );
}

// ── Card 1: AI Assistant ─────────────────────────────
function AICard() {
  const messages = [
    { role: 'user', text: 'Summarize key risks in this contract' },
    { role: 'ai', text: 'Found 3 critical clauses: liability cap at $50K, auto-renewal in §4.2, and missing SLA penalty terms. Recommend review before signing.' },
    { role: 'user', text: 'Draft a counter-proposal for the liability clause' },
    { role: 'ai', text: 'Drafting... liability cap increased to $500K with carve-outs for gross negligence and IP infringement...' },
  ];
  return (
    <div className="flex flex-col gap-2.5 p-3.5">
      {messages.map((m, i) => (
        <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
          {m.role === 'ai' && (
            <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'linear-gradient(135deg, #635BFF, #00C7FF)', fontSize: 10, color: '#fff', fontWeight: 700 }}>
              S
            </div>
          )}
          <div
            className="text-[11px] leading-relaxed rounded-2xl px-3 py-2 max-w-[80%]"
            style={{
              background: m.role === 'user' ? 'linear-gradient(135deg, #635BFF, #0073E6)' : '#F9FAFB',
              color: m.role === 'user' ? '#fff' : '#374151',
              borderRadius: m.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
            }}
          >
            {m.text}
          </div>
        </div>
      ))}
      {/* Typing indicator */}
      <div className="flex gap-2">
        <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg, #635BFF, #00C7FF)', fontSize: 10, color: '#fff', fontWeight: 700 }}>S</div>
        <div className="flex items-center gap-1 px-3 py-2 rounded-2xl" style={{ background: '#F9FAFB', borderRadius: '18px 18px 18px 4px' }}>
          {[0,1,2].map(i => (
            <div key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: '#9CA3AF', animation: `bounce 1s ${i*0.15}s infinite` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Card 2: Code Editor ──────────────────────────────
function CodeCard() {
  return (
    <div className="p-4" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
      <div className="text-[11px] leading-6">
        <div><span style={{color:'#635BFF'}}>import</span> <span style={{color:'#0A0527'}}>{`{ openai }`}</span> <span style={{color:'#635BFF'}}>from</span> <span style={{color:'#059669'}}>'@ai/sdk'</span></div>
        <div className="mt-2"><span style={{color:'#635BFF'}}>async function</span> <span style={{color:'#0073E6'}}>analyzeDocument</span><span style={{color:'#374151'}}>(doc) {'{'}</span></div>
        <div style={{paddingLeft:16}}><span style={{color:'#635BFF'}}>const</span> <span style={{color:'#0A0527'}}>result</span> <span style={{color:'#374151'}}>= await openai.</span><span style={{color:'#0073E6'}}>chat</span><span style={{color:'#374151'}}>({'{'}</span></div>
        <div style={{paddingLeft:32}}><span style={{color:'#059669'}}>model</span><span style={{color:'#374151'}}>: </span><span style={{color:'#059669'}}>"gpt-4o"</span><span style={{color:'#374151'}}>,</span></div>
        <div style={{paddingLeft:32}}><span style={{color:'#059669'}}>messages</span><span style={{color:'#374151'}}>: [{'{'}role: </span><span style={{color:'#059669'}}>"user"</span><span style={{color:'#374151'}}>, content: doc{'}'}]</span></div>
        <div style={{paddingLeft:16}}><span style={{color:'#374151'}}>{'}'})</span></div>
        <div style={{paddingLeft:16}}><span style={{color:'#635BFF'}}>return</span> <span style={{color:'#374151'}}>result.choices[0].message</span></div>
        <div><span style={{color:'#374151'}}>{'}'}</span></div>
        <div className="mt-2 flex items-center gap-2">
          <div className="w-2 h-4 rounded-sm" style={{background:'#635BFF', animation:'pulse 1s infinite'}} />
        </div>
      </div>
    </div>
  );
}

// ── Card 3: Dashboard Metrics ────────────────────────
function MetricsCard() {
  const bars = [65, 80, 45, 90, 70, 85, 60];
  return (
    <div className="p-4 flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2">
        {[
          { label: 'API Uptime', val: '99.98%', up: true, color: '#635BFF' },
          { label: 'Response Time', val: '142ms', up: false, color: '#0073E6' },
          { label: 'Active Users', val: '12,847', up: true, color: '#00D4AA' },
          { label: 'AI Requests', val: '2.4M', up: true, color: '#FC47BD' },
        ].map(m => (
          <div key={m.label} className="rounded-xl p-2.5" style={{ background: '#F9FAFB' }}>
            <div className="text-[10px] font-medium" style={{ color: '#9CA3AF' }}>{m.label}</div>
            <div className="text-[13px] font-bold mt-0.5" style={{ color: m.color }}>{m.val}</div>
            <div className="text-[9px] font-medium mt-0.5" style={{ color: m.up ? '#10B981' : '#EF4444' }}>
              {m.up ? '↑' : '↓'} {m.up ? '+12%' : '-2%'}
            </div>
          </div>
        ))}
      </div>
      {/* Mini bar chart */}
      <div className="flex items-end gap-1 h-10">
        {bars.map((h, i) => (
          <div key={i} className="flex-1 rounded-sm" style={{ height: `${h}%`, background: i === 3 ? 'linear-gradient(135deg, #635BFF, #00C7FF)' : 'rgba(99,91,255,0.15)', transition: 'height 300ms ease' }} />
        ))}
      </div>
    </div>
  );
}

// ── Card 4: Design System ────────────────────────────
function DesignCard() {
  return (
    <div className="p-4 flex flex-col gap-3">
      {/* Color palette */}
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#9CA3AF' }}>Brand Colors</div>
        <div className="flex gap-1.5">
          {['#635BFF','#0073E6','#00C7FF','#FC47BD','#00D4AA','#F9FAFB'].map(c => (
            <div key={c} className="w-6 h-6 rounded-lg shadow-sm" style={{ background: c, border: c === '#F9FAFB' ? '1px solid #E5E7EB' : 'none' }} />
          ))}
        </div>
      </div>
      {/* Typography */}
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#9CA3AF' }}>Typography</div>
        <div className="text-[18px] font-black" style={{ color: '#0A0527', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.03em', lineHeight: 1 }}>Display</div>
        <div className="text-[13px] font-semibold mt-0.5" style={{ color: '#374151' }}>Heading / Semibold</div>
        <div className="text-[11px]" style={{ color: '#6B7280' }}>Body text / Regular weight</div>
      </div>
      {/* Component preview */}
      <div className="flex gap-2">
        <div className="flex-1 py-1.5 px-3 rounded-full text-[10px] font-semibold text-white text-center" style={{ background: 'linear-gradient(135deg, #635BFF, #0073E6)' }}>Primary</div>
        <div className="flex-1 py-1.5 px-3 rounded-full text-[10px] font-semibold text-center" style={{ border: '1px solid rgba(99,91,255,0.3)', color: '#635BFF' }}>Secondary</div>
      </div>
    </div>
  );
}

// ── Card 5: Transformation Pipeline ─────────────────
function PipelineCard() {
  const steps = [
    { icon: '⚙️', label: 'Legacy System', status: 'migrating' },
    { icon: '🔄', label: 'Data Transform', status: 'running' },
    { icon: '☁️', label: 'Cloud Deploy', status: 'done' },
    { icon: '🤖', label: 'AI Layer', status: 'done' },
  ];
  return (
    <div className="p-4 flex flex-col gap-2.5">
      <div className="text-[11px] font-semibold" style={{ color: '#374151' }}>Digital Transformation Pipeline</div>
      {steps.map((s, i) => (
        <div key={s.label} className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-xl flex items-center justify-center text-sm shrink-0" style={{ background: s.status === 'done' ? 'rgba(16,185,129,0.1)' : s.status === 'running' ? 'rgba(99,91,255,0.1)' : 'rgba(251,146,60,0.1)' }}>
            {s.icon}
          </div>
          <div className="flex-1">
            <div className="text-[11px] font-semibold" style={{ color: '#374151' }}>{s.label}</div>
            <div className="h-1 rounded-full mt-1 overflow-hidden" style={{ background: 'rgba(0,0,0,0.06)' }}>
              <div className="h-full rounded-full" style={{ width: s.status === 'done' ? '100%' : s.status === 'running' ? '65%' : '30%', background: s.status === 'done' ? '#10B981' : s.status === 'running' ? 'linear-gradient(90deg, #635BFF, #00C7FF)' : '#FB923C', transition: 'width 1s ease' }} />
            </div>
          </div>
          <span className="text-[10px] font-medium" style={{ color: s.status === 'done' ? '#10B981' : s.status === 'running' ? '#635BFF' : '#FB923C' }}>
            {s.status === 'done' ? '✓' : s.status === 'running' ? 'Active' : '30%'}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Dialog Card wrapper ──────────────────────────────
function DialogCard({ title, color, children, delay, rotate = 0, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40, rotate: rotate * 0.5 }}
      animate={inView ? { opacity: 1, y: 0, rotate } : {}}
      transition={{ delay, duration: 0.65, ease }}
      whileHover={{ y: -8, rotate: 0, scale: 1.02, zIndex: 10 }}
      className={`rounded-2xl overflow-hidden ${className}`}
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <WindowChrome title={title} color={color} />
      {children}
    </motion.div>
  );
}

// ── Main Section ─────────────────────────────────────
export default function ShowcaseDialogs() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section className="px-5 sm:px-8 md:px-12 lg:px-20 py-24 section-mesh" style={{ overflow: 'hidden' }}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div ref={ref} className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.45, ease }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[11px] font-semibold mb-5"
            style={{ background: 'rgba(99,91,255,0.08)', border: '1px solid rgba(99,91,255,0.2)', color: '#635BFF' }}
          >
            What We Build
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55, delay: 0.08, ease }}
            className="text-4xl md:text-5xl lg:text-[3.5rem] font-black tracking-tight leading-[1.08]"
            style={{ color: '#0A0527', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.03em' }}
          >
            One partner for <span className="grad-text">every layer</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.16, ease }}
            className="text-lg mt-4 max-w-2xl mx-auto leading-relaxed"
            style={{ color: '#6B7280' }}
          >
            From AI assistants to design systems to cloud migrations — we ship all of it, end to end.
          </motion.p>
        </div>

        {/* Desktop: floating scattered cards */}
        <div className="hidden lg:block relative" style={{ height: 520 }}>
          {/* Card 1 — AI Assistant, top-left */}
          <div style={{ position: 'absolute', top: 0, left: '2%', width: 280 }}>
            <DialogCard title="Syenovix AI Assistant" color="purple" delay={0.1} rotate={-1.5}>
              <AICard />
            </DialogCard>
          </div>

          {/* Card 2 — Code Editor, top-center */}
          <div style={{ position: 'absolute', top: 30, left: '30%', width: 300 }}>
            <DialogCard title="api/analyze.ts" color="blue" delay={0.2} rotate={1}>
              <CodeCard />
            </DialogCard>
          </div>

          {/* Card 3 — Dashboard, top-right */}
          <div style={{ position: 'absolute', top: 0, right: '2%', width: 280 }}>
            <DialogCard title="Product Analytics" color="cyan" delay={0.3} rotate={1.5}>
              <MetricsCard />
            </DialogCard>
          </div>

          {/* Card 4 — Design System, bottom-left */}
          <div style={{ position: 'absolute', bottom: 0, left: '12%', width: 260 }}>
            <DialogCard title="Design System" color="pink" delay={0.4} rotate={-1}>
              <DesignCard />
            </DialogCard>
          </div>

          {/* Card 5 — Pipeline, bottom-right */}
          <div style={{ position: 'absolute', bottom: 0, right: '12%', width: 300 }}>
            <DialogCard title="Transformation Pipeline" color="teal" delay={0.5} rotate={1}>
              <PipelineCard />
            </DialogCard>
          </div>
        </div>

        {/* Mobile: responsive grid */}
        <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { title: 'Syenovix AI Assistant', color: 'purple', content: <AICard /> },
            { title: 'api/analyze.ts', color: 'blue', content: <CodeCard /> },
            { title: 'Product Analytics', color: 'cyan', content: <MetricsCard /> },
            { title: 'Design System', color: 'pink', content: <DesignCard /> },
            { title: 'Transformation Pipeline', color: 'teal', content: <PipelineCard /> },
          ].map((c, i) => (
            <DialogCard key={c.title} title={c.title} color={c.color} delay={i * 0.1}>
              {c.content}
            </DialogCard>
          ))}
        </div>
      </div>
    </section>
  );
}
